import { Fragment, type ReactNode } from "react";
import {
  outsideLimit, formatLimitLine, formatLimitsInline, PERMIT_LIMIT_SEPARATOR, permitAlertState,
  PERMIT_ALERT_TEXT, PERMIT_ALERT_CLEAR, type PermitLimit,
} from "./permitLimit";
import "./permitLimit.css";

/**
 * Stage 3 (Permit-To-Notification) — the shared stacked permit-limit cell.
 *
 * Renders every applicable permit limit on its own line and colours a line RED
 * when the result is OUTSIDE it (permitLimit.ts — ALPACA compares/flags, never
 * determines compliance; the colour carries no word). Falls back to `fallback`
 * (the scalar permit-limit string) when the row has no structured limits — e.g.
 * a job registered without a permit — so a no-permit row renders exactly as before
 * and raises no flag.
 *
 * Used by both Result-Entry-Express grids (`<AppDataGrid>` column `render`) and
 * the SDMS batch-results grid (`SdmsPackageDetail`, inside its own `<td>`).
 */
export function renderPermitLimitCell(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  units: string | null | undefined,
  fallback: string,
): ReactNode {
  if (!limits || limits.length === 0) return fallback;
  // ONE line, limits side by side (user request 2026-09-27 — the column widens to fit
  // instead of stacking; permitLimitWidth.ts). The cell tooltip carries the whole value
  // so it stays readable if the column hits its max and wraps between limits.
  return (
    <span className="plim-cell" title={formatLimitsInline(limits)}>
      {limits.map((l, i) => {
        const out = outsideLimit(l, result, units) === true;
        return (
          <Fragment key={i}>
            {i > 0 && <span className="plim-sep">{PERMIT_LIMIT_SEPARATOR}</span>}
            <span
              className={out ? "plim-line plim-out" : "plim-line"}
              title={l.alertSource ? `${formatLimitLine(l)} · Source: ${l.alertSource}` : formatLimitLine(l)}
            >
              {formatLimitLine(l)}
            </span>
          </Fragment>
        );
      })}
    </span>
  );
}

/**
 * The shared Alert-column cell for REE / RT / RV / RA (RTCFR "Display 'Notify lab' in
 * Alert Column", 2026-10-13): a red flag + red "Notify lab" when any applicable permit
 * limit is violated (`permitAlertState` — the same test that reds a limit line), a muted
 * "—" when the row has limits and a result but violates none, else the row's own Alert
 * value (`fallback`, blank today). Recomputed on every render, so it follows an edited
 * result immediately and is correct again after Save / Refresh / reopen.
 */
export function renderPermitAlertCell(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  numeric: string | number | null | undefined,
  units: string | null | undefined,
  fallback: string | null | undefined,
): ReactNode {
  const state = permitAlertState(limits, result, numeric, units);
  if (state === "notify") {
    return (
      <span className="plim-alert" title="A result is outside an applicable permit limit — notify the lab">
        <span className="plim-alert-flag" aria-hidden="true">⚑</span> {PERMIT_ALERT_TEXT}
      </span>
    );
  }
  if (state === "clear") return <span className="plim-alert-clear">{PERMIT_ALERT_CLEAR}</span>;
  return fallback ?? "";
}
